// @ts-nocheck
/**
 * Semantic validation for ElementDefinition.editForms (Predefined Edit Forms).
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue, makePassResult } from '../../diagnostics';
import type {
  DataRef,
  EditFormDefinition,
  EditFormRow,
  ElementDefinition,
  TemplateDocument,
  ValueSource,
} from '../../types/canonical';
import { EDIT_FORM_ROW_KINDS } from '../../types/editForm';

const ROW_KIND_SET = new Set<string>(EDIT_FORM_ROW_KINDS);

function isValueSource(value: unknown): value is ValueSource<unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const kind = (value as { kind?: unknown }).kind;
  if (kind === 'static') return 'value' in (value as object);
  if (kind === 'binding') {
    const ref = (value as { ref?: unknown }).ref;
    return ref !== null && typeof ref === 'object' && typeof (ref as { kind?: unknown }).kind === 'string';
  }
  if (kind === 'expression') {
    return typeof (value as { expressionId?: unknown }).expressionId === 'string';
  }
  return false;
}

function isLocalizedText(value: unknown): boolean {
  if (typeof value === 'string') return true;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((v) => typeof v === 'string');
}

function isEditFormText(value: unknown): boolean {
  return isLocalizedText(value) || isValueSource(value);
}

function isDataRef(value: unknown): value is DataRef {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof (value as { kind?: unknown }).kind === 'string'
  );
}

function validateVisibility(
  result: ValidationResult,
  visibility: unknown,
  path: string,
  elementId: string,
): ValidationResult {
  if (visibility === undefined) return result;
  if (visibility === null || typeof visibility !== 'object' || Array.isArray(visibility)) {
    return addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_edit_form_row_visibility',
      message: `Element '${elementId}' edit form row visibility must be an object`,
      path,
    });
  }
  if (!isValueSource((visibility as { visible?: unknown }).visible)) {
    return addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_edit_form_row_visibility',
      message: `Element '${elementId}' edit form row visibility.visible must be a ValueSource`,
      path: `${path}.visible`,
    });
  }
  return result;
}

function validateRow(
  result: ValidationResult,
  row: EditFormRow,
  path: string,
  elementId: string,
  formId: string,
  seenRowIds: Set<string>,
): ValidationResult {
  if (row === null || typeof row !== 'object' || Array.isArray(row)) {
    return addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_edit_form_row',
      message: `Element '${elementId}' editForms['${formId}'] row must be an object`,
      path,
    });
  }

  if (typeof row.id !== 'string' || row.id.trim() === '') {
    result = addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_edit_form_row',
      message: `Element '${elementId}' editForms['${formId}'] row id must be a non-empty string`,
      path: `${path}.id`,
    });
  } else if (seenRowIds.has(row.id)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'semantic.duplicate_edit_form_row_id',
      message: `Element '${elementId}' editForms['${formId}'] has duplicate row id '${row.id}'`,
      path: `${path}.id`,
    });
  } else {
    seenRowIds.add(row.id);
  }

  if (!ROW_KIND_SET.has(row.kind)) {
    return addIssue(result, {
      severity: 'error',
      code: 'semantic.invalid_edit_form_row_kind',
      message: `Element '${elementId}' editForms['${formId}'] row kind '${String(row.kind)}' is unknown`,
      path: `${path}.kind`,
    });
  }

  result = validateVisibility(result, row.visibility, `${path}.visibility`, elementId);

  switch (row.kind) {
    case 'chrome': {
      if (row.title !== undefined && !isEditFormText(row.title)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_row_text',
          message: `Element '${elementId}' edit form chrome title must be LocalizedText or ValueSource`,
          path: `${path}.title`,
        });
      }
      break;
    }
    case 'groupTitle':
    case 'groupSubtitle':
    case 'propertyTitle':
    case 'propertySubtitle': {
      if (!isEditFormText(row.text)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_row_text',
          message: `Element '${elementId}' edit form label row text must be LocalizedText or ValueSource`,
          path: `${path}.text`,
        });
      }
      break;
    }
    case 'field': {
      if (!isDataRef(row.target)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_field_target',
          message: `Element '${elementId}' edit form field target must be a DataRef`,
          path: `${path}.target`,
        });
      } else if (row.target.kind !== 'itemField' && row.target.kind !== 'categoryProperty') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_field_target',
          message: `Element '${elementId}' edit form field target must be itemField or categoryProperty`,
          path: `${path}.target`,
        });
      } else if (row.target.kind === 'itemField') {
        if (typeof row.target.field !== 'string' || row.target.field.trim() === '') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_edit_form_field_target',
            message: `Element '${elementId}' edit form itemField target.field must be a non-empty string`,
            path: `${path}.target.field`,
          });
        }
      } else if (row.target.kind === 'categoryProperty') {
        if (typeof row.target.categoryId !== 'string' || row.target.categoryId.trim() === '') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_edit_form_field_target',
            message: `Element '${elementId}' edit form categoryProperty target.categoryId must be a non-empty string`,
            path: `${path}.target.categoryId`,
          });
        }
        if (typeof row.target.propertyId !== 'string' || row.target.propertyId.trim() === '') {
          result = addIssue(result, {
            severity: 'error',
            code: 'semantic.invalid_edit_form_field_target',
            message: `Element '${elementId}' edit form categoryProperty target.propertyId must be a non-empty string`,
            path: `${path}.target.propertyId`,
          });
        }
      }
      break;
    }
    case 'images': {
      break;
    }
    case 'documents': {
      break;
    }
    case 'spacer': {
      if (typeof row.height !== 'number' || !Number.isFinite(row.height) || row.height < 0) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_spacer',
          message: `Element '${elementId}' edit form spacer height must be a non-negative number`,
          path: `${path}.height`,
        });
      }
      break;
    }
    case 'divider': {
      if (
        typeof row.thickness !== 'number' ||
        !Number.isFinite(row.thickness) ||
        row.thickness < 0
      ) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_divider',
          message: `Element '${elementId}' edit form divider thickness must be a non-negative number`,
          path: `${path}.thickness`,
        });
      }
      break;
    }
  }

  return result;
}

export function checkElementEditForms(doc: TemplateDocument): ValidationResult {
  let result = makePassResult();

  for (let i = 0; i < doc.elements.length; i++) {
    const element = doc.elements[i] as ElementDefinition;
    const editForms = element.editForms;
    if (editForms === undefined) continue;

    const pathBase = `elements[${i}].editForms`;

    if (!Array.isArray(editForms)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'semantic.invalid_edit_forms',
        message: `Element '${element.id}' editForms must be an array`,
        path: pathBase,
      });
      continue;
    }

    const seenFormIds = new Set<string>();

    for (let fi = 0; fi < editForms.length; fi++) {
      const form = editForms[fi] as EditFormDefinition;
      const formPath = `${pathBase}[${fi}]`;

      if (form === null || typeof form !== 'object' || Array.isArray(form)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form',
          message: `Element '${element.id}' editForms[${fi}] must be an object`,
          path: formPath,
        });
        continue;
      }

      if (typeof form.id !== 'string' || form.id.trim() === '') {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form',
          message: `Element '${element.id}' editForms[${fi}].id must be a non-empty string`,
          path: `${formPath}.id`,
        });
      } else if (seenFormIds.has(form.id)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.duplicate_edit_form_id',
          message: `Element '${element.id}' has duplicate editForm id '${form.id}'`,
          path: `${formPath}.id`,
        });
      } else {
        seenFormIds.add(form.id);
      }

      if (form.displayName !== undefined && !isLocalizedText(form.displayName)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form',
          message: `Element '${element.id}' editForms[${fi}].displayName must be LocalizedText`,
          path: `${formPath}.displayName`,
        });
      }

      if (!Array.isArray(form.rows)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'semantic.invalid_edit_form_rows',
          message: `Element '${element.id}' editForms[${fi}].rows must be an array`,
          path: `${formPath}.rows`,
        });
        continue;
      }

      const seenRowIds = new Set<string>();
      for (let ri = 0; ri < form.rows.length; ri++) {
        result = validateRow(
          result,
          form.rows[ri] as EditFormRow,
          `${formPath}.rows[${ri}]`,
          element.id,
          typeof form.id === 'string' ? form.id : String(fi),
          seenRowIds,
        );
      }
    }
  }

  return result;
}
